// @flow
import DesktopTraceEventHandler from 'app/common/lib/TraceLogger/DesktopTraceEventHandler';
import HANDLER_CONFIGS from 'app/common/lib/TraceLogger/config';
import traceLogger from 'app/packages/trace-logger/singleton';
import { addAppInitAnnotation } from 'app/common/lib/TraceLogger/appInitAnnotations';
import { handleRouteChange } from 'app/packages/trace-logger/resourceTiming';
import type {
  StartTraceEvent,
  StopTraceEvent,
  AbortTraceEvent,
  CustomTraceEvent,
  PageReactRouterOnChangeTraceEvent,
  MetricName,
  AbortReason,
} from 'app/common/lib/TraceLogger/types';

const addHandlerIfNecessary = (metricName: MetricName) => {
  if (traceLogger.isDisabled()) {
    return;
  }
  const matchedConfig = HANDLER_CONFIGS.find((config) => metricName === config.metricName);
  if (!matchedConfig) {
    console.warn(`Trace for ${metricName} not found`); // eslint-disable-line no-console
    return;
  }
  if (!traceLogger.findHandlerByName(matchedConfig.traceName)) {
    traceLogger.addHandler(new DesktopTraceEventHandler(matchedConfig));
  }
};

export const startTiming = (metricName: MetricName) => {
  addHandlerIfNecessary(metricName);
  const event: StartTraceEvent = {
    name: 'start',
    metricName,
  };
  traceLogger.sendEvent(event);
};

export const stopTiming = (
  metricName: MetricName,
  options?: {|
    error?: boolean,
    viewParameter?: number,
    gridImagesCached?: boolean,
  |},
) => {
  const event: StopTraceEvent = {
    name: 'stop',
    metricName,
    ...options,
  };
  traceLogger.sendEvent(event);
};

export const abortTiming = (reason: AbortReason, metricName?: MetricName) => {
  const event: AbortTraceEvent = {
    name: 'abort',
    reason,
    metricName,
  };
  traceLogger.sendEvent(event);
};

export const logRouteChanged = () => {
  const event: PageReactRouterOnChangeTraceEvent = { name: 'PageReactRouterOnChangeTraceEvent' };
  traceLogger.sendEvent(event);
  handleRouteChange();
};

export const logCustomTraceEvent = (event: CustomTraceEvent) => {
  traceLogger.sendEvent(event);
};

export { addAppInitAnnotation };

let initialPageLoadMetric: MetricName | void;

export const startInitialPageLoadTiming = (
  metricName: MetricName,
  pageLoadData: {|
    spanId: ?string,
    traceId: ?string,
  |},
) => {
  initialPageLoadMetric = metricName;
  addHandlerIfNecessary(metricName);
  const event: StartTraceEvent = {
    name: 'start',
    metricName,
    ...pageLoadData,
  };
  traceLogger.sendEvent(event);
};

// A page-load event handler expects to receive a stop event either
// 1) when App component cDM is called (AppDidMount), or
// 2) when window load event is called (WindowOnLoad).
// The former case (AppDidMount) is simpler and works for most cases.
// The latter case (WindowOnLoad) is only used when the end time depends on specific events that
// can happen after the App component mounts, such as Masonry visually complete.
type PageLoadEndTimeType = 'AppDidMount' | 'WindowOnLoad';

const stopInitialPageLoadTiming = (type: PageLoadEndTimeType) => {
  if (!initialPageLoadMetric) {
    return;
  }
  const config = HANDLER_CONFIGS.find((config) => initialPageLoadMetric === config.metricName);
  if (!config) {
    return;
  }
  const { measureMasonry, endTimeConstraints } = config;
  const expectedType =
    measureMasonry || (endTimeConstraints || []).length > 0 ? 'WindowOnLoad' : 'AppDidMount';
  if (type !== expectedType) {
    return;
  }
  const event: StopTraceEvent = {
    name: 'stop',
    metricName: initialPageLoadMetric,
  };
  traceLogger.sendEvent(event);
};

export const windowDidLoad = () => stopInitialPageLoadTiming('WindowOnLoad');

export const appDidMount = () => stopInitialPageLoadTiming('AppDidMount');
